import {
	LIST_ITEM_KEY,
	UNORDERED_LIST_KEY,
	ORDERED_LIST_KEY,
	PARAGRAPH_KEY
} from '@techchange/pm-specs';
import {
	liftListItem as _liftListItem,
	sinkListItem as _sinkListItem,
	splitListItem as _splitListItem,
} from 'prosemirror-schema-list';
import { TextSelection } from 'prosemirror-state';

import { toggleList } from './toggleList';

/**
 * Moves list item out of list by one level.
 *
 * @param {Object} state     The current editor state.
 * @param {Object} dispatch  The editor dispatch.
 *
 * @returns {boolean} Whether or not the command was successful.
 *
 */
export const liftListItem = (state, dispatch) => {

	const listItemNodeType = state.schema.nodes[LIST_ITEM_KEY];

	return _liftListItem(listItemNodeType)(state, dispatch);

};

/**
 * Moves list item into list by one level.
 *
 * @param {Object} state     The current editor state.
 * @param {Object} dispatch  The editor dispatch.
 *
 * @returns {boolean} Whether or not the command was successful.
 *
 */
export const sinkListItem = (state, dispatch) => {

	const listItemNodeType = state.schema.nodes[LIST_ITEM_KEY];

	return _sinkListItem(listItemNodeType)(state, dispatch);

};

/**
 * Splits non-empty text node inside a list item.
 *
 * @param {Object} state     The current editor state.
 * @param {Object} dispatch  The editor dispatch.
 *
 * @returns {boolean} Whether or not the command was successful.
 *
 */
export const splitListItem = (state, dispatch) => {

	const listItemNodeType = state.schema.nodes[LIST_ITEM_KEY];

	return _splitListItem(listItemNodeType)(state, dispatch);

};

/**
 * Toggles an unordered list.
 *
 * @param {Object} state     The current editor state.
 * @param {Object} dispatch  The editor dispatch.
 *
 * @returns {boolean} Whether or not the command was successful.
 *
 */
export const toggleUnorderedList = (state, dispatch) => {

	const unorderedListNodeType = state.schema.nodes[UNORDERED_LIST_KEY];

	return toggleList(unorderedListNodeType)(state, dispatch);

};

/**
 * Toggles an ordered list.
 *
 * @param {Object} state     The current editor state.
 * @param {Object} dispatch  The editor dispatch.
 *
 * @returns {boolean} Whether or not the command was successful.
 */
export const toggleOrderedList = (state, dispatch) => {

	const orderedListNodeType = state.schema.nodes[ORDERED_LIST_KEY];

	return toggleList(orderedListNodeType)(state, dispatch);

};

/**
 * Moves cursor to the end of the last item in a list when the node
 * preceding the selection cursor is a list node.
 *
 * @param {Object} state     The current editor state.
 * @param {Object} dispatch  The editor dispatch.
 *
 * @returns {boolean} Whether or not the command was successful.
 *
 */
export const backspaceIntoList = (state, dispatch) => {

	try {

		const { tr, schema, selection, doc } = state;

		// Node type constants
		const paragraphNodeType = schema.nodes[PARAGRAPH_KEY];
		const unorderedListNodeType = schema.nodes[UNORDERED_LIST_KEY];
		const orderedListNodeType = schema.nodes[ORDERED_LIST_KEY];

		// Find resolved position outside wrapping paragraph node of the text selection
		const cutPos = selection.from - 1;
		const $cut = doc.resolve(cutPos);

		// Get nodes before and after the selection position
		const beforeNode = $cut && $cut.nodeBefore;
		const afterNode = $cut && $cut.nodeAfter;

		const listBefore = beforeNode && (beforeNode.type === unorderedListNodeType ||
			beforeNode.type === orderedListNodeType);
		const paragraphAfter = afterNode && afterNode.type === paragraphNodeType;

		if (listBefore && paragraphAfter) {

			// Find end position of the last text node in the list
			let lastTextNodePos;
			const listStart = cutPos - beforeNode.nodeSize, listEnd = cutPos;

			doc.nodesBetween(listStart, listEnd, (node, pos) => {

				lastTextNodePos = pos + node.nodeSize;
				return true;

			});

			// Move paragraph node content to the end of the last list item
			tr.delete(cutPos, cutPos + afterNode.nodeSize);
			tr.insert(lastTextNodePos, afterNode.content.content);

			// Update the selection to be at the end of the last text node in the list
			const newSelection = TextSelection.create(tr.doc, lastTextNodePos);

			tr.setSelection(newSelection);

			if (dispatch) {

				dispatch(tr);

			}

			return true;

		}

		return false;

	// Resort to default backspace behavior if we run into an error
	} catch (err) {

		return false;
	}

};
